import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Dimensions,
  Easing,
  Modal,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { RFValue } from 'react-native-responsive-fontsize';
import { computeFilterPickerPanelLayout } from '../FilterBar/filterPickerLayout';
import { Text } from '../FilterBar/style';

const PANEL_BOTTOM_MARGIN = RFValue(88);
const PANEL_BORDER_RADIUS = RFValue(12);
const BACKDROP_FADE_HEIGHT = RFValue(20);
const BACKDROP_COLOR = 'rgba(0,0,0,0.32)';

function withAlpha(color, alphaHex) {
  if (!color.startsWith('#') || color.length !== 7) {
    return color;
  }
  return `${color}${alphaHex}`;
}

export const FilterPickerDrawer = memo(
  ({
    visible,
    onClose,
    items,
    onSelect,
    actions = [],
    colors,
    fonts,
    fontSize,
    anchor,
    fallbackTop = 0,
  }) => {
    const insets = useSafeAreaInsets();
    const window = Dimensions.get('window');

    const layout = useMemo(
      () => computeFilterPickerPanelLayout(anchor, window.width, fallbackTop),
      [anchor, window.width, fallbackTop]
    );

    const panelMaxHeight =
      window.height - layout.panelTop - insets.bottom - PANEL_BOTTOM_MARGIN;

    const [mounted, setMounted] = useState(false);
    const slideAnim = useRef(new Animated.Value(layout.slideFrom)).current;
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const backdropAnim = useRef(new Animated.Value(0)).current;

    const rows = useMemo(
      () => [
        ...items.map((item) => ({
          key: item.value ?? '__all__',
          kind: 'item',
          label: item.label,
          value: item.value,
        })),
        ...actions.map((action) => ({
          key: `action:${action.label}`,
          kind: 'action',
          label: action.label,
          onPress: action.onPress,
        })),
      ],
      [items, actions]
    );

    useEffect(() => {
      if (visible) {
        setMounted(true);
        slideAnim.setValue(layout.slideFrom);
        fadeAnim.setValue(0);
        backdropAnim.setValue(0);
        Animated.parallel([
          Animated.timing(backdropAnim, {
            toValue: 1,
            duration: 200,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 220,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
          Animated.timing(slideAnim, {
            toValue: 0,
            duration: 260,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
        ]).start();
        return;
      }

      if (!mounted) return;

      Animated.parallel([
        Animated.timing(backdropAnim, {
          toValue: 0,
          duration: 180,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 180,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: layout.slideFrom,
          duration: 220,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start(({ finished }) => {
        if (finished) setMounted(false);
      });
    }, [visible, mounted, slideAnim, fadeAnim, backdropAnim, layout.slideFrom]);

    const handleSelect = (value) => {
      onSelect(value);
      onClose();
    };

    const handleAction = (action) => {
      action.onPress();
      onClose();
    };

    if (!mounted) return null;

    const panelBackground = withAlpha(colors.background, 'EE');

    return (
      <Modal visible={mounted} animationType="none" transparent onRequestClose={onClose}>
        <View style={styles.root} pointerEvents="box-none">
          <Animated.View
            pointerEvents={visible ? 'auto' : 'none'}
            style={[
              styles.backdrop,
              {
                top: layout.panelTop - BACKDROP_FADE_HEIGHT,
                opacity: backdropAnim,
              },
            ]}
          >
            <TouchableWithoutFeedback onPress={onClose}>
              <View style={styles.backdropTouchable}>
                <LinearGradient
                  colors={['transparent', BACKDROP_COLOR]}
                  style={{ height: BACKDROP_FADE_HEIGHT }}
                />
                <View style={styles.backdropFill} />
              </View>
            </TouchableWithoutFeedback>
          </Animated.View>

          <Animated.View
            style={[
              styles.panel,
              {
                top: layout.panelTop,
                left: layout.panelLeft,
                width: layout.panelWidth,
                maxHeight: panelMaxHeight,
                borderRadius: PANEL_BORDER_RADIUS,
                backgroundColor: panelBackground,
                opacity: fadeAnim,
                transform: [{ translateX: slideAnim }],
              },
            ]}
          >
            <ScrollView
              showsVerticalScrollIndicator
              bounces
              style={styles.list}
              contentContainerStyle={styles.listContent}
            >
              {rows.map((row) => (
                <TouchableOpacity
                  key={row.key}
                  activeOpacity={0.72}
                  onPress={() =>
                    row.kind === 'item'
                      ? handleSelect(row.value)
                      : handleAction(row)
                  }
                  style={styles.row}
                >
                  <Text
                    fontFamily={fonts}
                    fontSize={fontSize + 2}
                    fontWeight="normal"
                    color={colors.text}
                  >
                    {row.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>
    );
  }
);

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  backdrop: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  backdropTouchable: {
    flex: 1,
  },
  backdropFill: {
    flex: 1,
    backgroundColor: BACKDROP_COLOR,
  },
  panel: {
    position: 'absolute',
    overflow: 'hidden',
  },
  list: {
    flexGrow: 0,
  },
  listContent: {
    paddingVertical: RFValue(2),
  },
  row: {
    paddingVertical: RFValue(14),
    paddingHorizontal: RFValue(18),
  },
});

FilterPickerDrawer.displayName = 'FilterPickerDrawer';

export default FilterPickerDrawer;
